import { useState, useCallback, useEffect, useRef } from 'react'
import { notificationsClient } from '../services/orgAppClient'
import type { Notification } from '../types/notifications'

interface UseNotificationsResult {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  hasMore: boolean
  markRead: (id: string) => void
  markAllRead: () => void
  archiveAll: () => void
  loadMore: () => void
  loadPage: (pageNum: number, append?: boolean) => void
  refreshUnreadCount: () => void
}

const POLL_INTERVAL_MS = 60_000

/**
 * Fired when a surface other than the bell (the procurement catch-up inbox)
 * changes notification read state, so the bell's count re-syncs without
 * waiting for the next poll.
 */
export const NOTIFICATIONS_CHANGED_EVENT = 'rochd:notifications-changed'

export function notifyNotificationsChanged(): void {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT))
}

function fireBrowserNotifications(items: Notification[], notifiedIds: Set<string>) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (localStorage.getItem('browserNotificationsEnabled') === 'false') return
  for (const item of items) {
    if (!notifiedIds.has(item.id)) {
      notifiedIds.add(item.id)
      const title = item.title
      const options = { body: item.body, icon: '/android-chrome-512x512.png', data: { url: item.entity_url || '/app' } }
      const sw = 'serviceWorker' in navigator ? navigator.serviceWorker.controller : null
      if (sw) {
        const fallback = () => { try { new window.Notification(title, options) } catch { /* ignore */ } }
        navigator.serviceWorker.ready
          .then((reg) => reg.showNotification(title, options))
          .catch(fallback)
      } else {
        try {
          const n = new window.Notification(title, options)
          n.onclick = () => {
            window.focus()
            const url = item.entity_url
            if (url) window.location.href = url
          }
        } catch { /* ignore */ }
      }
    }
  }
}

export function useNotifications(): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevCountRef = useRef<number | null>(null)
  const notifiedIdsRef = useRef<Set<string>>(new Set())

  const refreshUnreadCount = useCallback(async () => {
    try {
      const data = await notificationsClient.getUnreadCount()
      const newCount: number = data?.count ?? 0
      if (prevCountRef.current !== null && newCount > prevCountRef.current) {
        // New notifications arrived — fetch the latest unread ones for browser alerts
        notificationsClient.getNotifications(1, 5, true)
          .then((d) => fireBrowserNotifications(d?.items ?? [], notifiedIdsRef.current))
          .catch(() => { /* non-critical */ })
      }
      prevCountRef.current = newCount
      setUnreadCount(newCount)
    } catch {
      // silently ignore — unread count is non-critical
    }
  }, [])

  // Poll unread count every minute, and re-sync immediately when another
  // surface (catch-up inbox) changes read state.
  useEffect(() => {
    refreshUnreadCount()
    pollRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS)
    const onChanged = () => refreshUnreadCount()
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged)
    }
  }, [refreshUnreadCount])

  const loadPage = useCallback(async (pageNum: number, append = false) => {
    setLoading(true)
    try {
      const data = await notificationsClient.getNotifications(pageNum, 20)
      const items: Notification[] = data?.items ?? []
      const total: number = data?.total ?? 0
      setNotifications((prev) => (append ? [...prev, ...items] : items))
      setPage(pageNum)
      setHasMore(pageNum * 20 < total)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMore = useCallback(() => {
    loadPage(page + 1, true)
  }, [loadPage, page])

  const markRead = useCallback(async (id: string) => {
    try {
      await notificationsClient.markRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      )
      setUnreadCount((c) => Math.max(0, c - 1))
    } catch {
      // ignore
    }
  }, [])

  const markAllRead = useCallback(async () => {
    try {
      await notificationsClient.markAllRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch {
      // ignore
    }
  }, [])

  const archiveAll = useCallback(async () => {
    try {
      await notificationsClient.archiveAll()
      setNotifications([])
      setUnreadCount(0)
    } catch {
      // ignore
    }
  }, [])

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    markRead,
    markAllRead,
    archiveAll,
    loadMore,
    loadPage,
    refreshUnreadCount,
  }
}
